import { privateClient } from "@/api/privateClient";
import { INSTITUTION_ADMIN_ENDPOINTS } from "../../institutionAdmin.endpoints";
import type {
  InstitutionRole,
  CreateInstitutionRolePayload,
  UpdateInstitutionRolePayload,
  StaffMember,
  CreateStaffPayload,
  CreateStaffResponse,
  UpdateStaffPayload,
  StaffListResponse,
  StaffListParams,
  MarkStaffAttendancePayload,
  StaffAttendanceRecord,
  StaffAttendanceRosterResponse,
  SalaryComponent,
  CreateSalaryComponentPayload,
  SalaryStructure,
  UpsertSalaryStructurePayload,
  MonthlyPayable,
  StaffAssignment,
  CreateAssignmentPayload,
} from "../types";

/**
 * Institution Staff service — real API only, no mock toggle.
 * All endpoints are institution-scoped and require an authenticated
 * SUPER_ADMIN / ORG_ADMIN / CAMPUS_ADMIN / INSTITUTION_ADMIN actor.
 */

// ---------- Roles ----------

const getInstitutionRoles = async (
  institutionId: number
): Promise<InstitutionRole[]> => {
  const { data } = await privateClient.get<{ roles: InstitutionRole[] }>(
    INSTITUTION_ADMIN_ENDPOINTS.ROLES.LIST(institutionId)
  );
  return data.roles;
};

const createInstitutionRole = async (
  institutionId: number,
  payload: CreateInstitutionRolePayload
): Promise<InstitutionRole> => {
  const { data } = await privateClient.post<{ role: InstitutionRole }>(
    INSTITUTION_ADMIN_ENDPOINTS.ROLES.CREATE(institutionId),
    payload
  );
  return data.role;
};

// Only reaches this institution's own custom (DYNAMIC) roles — canonical
// fixed roles (isCanonical: true) are rejected by the backend with 404.
const updateInstitutionRole = async (
  institutionId: number,
  roleId: number,
  payload: UpdateInstitutionRolePayload
): Promise<InstitutionRole> => {
  const { data } = await privateClient.put<{ role: InstitutionRole }>(
    INSTITUTION_ADMIN_ENDPOINTS.ROLES.UPDATE(institutionId, roleId),
    payload
  );
  return data.role;
};

// Backend returns 409 if any staff/login still holds this role.
const deleteInstitutionRole = async (institutionId: number, roleId: number): Promise<void> => {
  await privateClient.delete(INSTITUTION_ADMIN_ENDPOINTS.ROLES.DELETE(institutionId, roleId));
};

// ---------- Staff ----------

const getStaff = async (
  institutionId: number,
  params?: StaffListParams
): Promise<StaffListResponse> => {
  const { all, ...query } = params ?? {};
  if (!all) {
    const { data } = await privateClient.get<StaffListResponse>(
      INSTITUTION_ADMIN_ENDPOINTS.STAFF.LIST(institutionId),
      { params: query }
    );
    return data;
  }

  let merged: StaffListResponse | null = null;
  let page = 1;
  let totalPages = 1;
  do {
    const { data } = await privateClient.get<StaffListResponse>(
      INSTITUTION_ADMIN_ENDPOINTS.STAFF.LIST(institutionId),
      { params: { ...query, page, limit: 100 } }
    );
    merged = merged ? { ...data, staff: [...merged.staff, ...data.staff] } : data;
    totalPages = data.pagination.totalPages;
    page += 1;
  } while (page <= totalPages);
  return merged as StaffListResponse;
};

/** Staff holding one canonical role (TEACHER/DRIVER/ACCOUNTANT/LIBRARIAN) — powers role-specific management pages. */
const getStaffByRole = async (institutionId: number, roleName: string): Promise<StaffMember[]> => {
  const { data } = await privateClient.get<{ staff: StaffMember[] }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.BY_ROLE(institutionId, roleName)
  );
  return data.staff;
};

const createStaff = async (
  institutionId: number,
  payload: CreateStaffPayload
): Promise<CreateStaffResponse> => {
  const { data } = await privateClient.post<CreateStaffResponse>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.CREATE(institutionId),
    payload
  );
  return data;
};

const getStaffById = async (institutionId: number, staffId: number): Promise<StaffMember> => {
  const { data } = await privateClient.get<{ staff: StaffMember }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.DETAIL(institutionId, staffId)
  );
  return data.staff;
};

const updateStaff = async (
  institutionId: number,
  staffId: number,
  payload: UpdateStaffPayload
): Promise<StaffMember> => {
  const { data } = await privateClient.put<{ staff: StaffMember }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.UPDATE(institutionId, staffId),
    payload
  );
  return data.staff;
};

const changeStaffRole = async (institutionId: number, staffId: number, roleId: number): Promise<StaffMember> => {
  const { data } = await privateClient.put<{ staff: StaffMember }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.CHANGE_ROLE(institutionId, staffId),
    { roleId }
  );
  return data.staff;
};

/** Admin-driven reset — generates a fresh temp password server-side, shown once, and revokes any active session for that login. */
const resetStaffPassword = async (userId: number): Promise<{ email: string; tempPassword: string }> => {
  const { data } = await privateClient.post<{ user: { email: string }; tempPassword: string }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.RESET_PASSWORD(userId)
  );
  return { email: data.user.email, tempPassword: data.tempPassword };
};

/** Admin-driven login-email change — independent of any contact-email field on the staff profile itself. Revokes any active session for that login. */
const changeStaffEmail = async (userId: number, email: string): Promise<{ user: { email: string } }> => {
  const { data } = await privateClient.post<{ user: { email: string } }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.CHANGE_EMAIL(userId),
    { email }
  );
  return { user: { email: data.user.email } };
};

/** Backfill path — issues a login for a staff member who doesn't have one yet (e.g. added before their role required one). */
const grantStaffLogin = async (
  institutionId: number,
  staffId: number,
  email: string
): Promise<{ email: string; tempPassword: string }> => {
  const { data } = await privateClient.post<{ credential: { email: string; tempPassword: string } }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.GRANT_LOGIN(institutionId, staffId),
    { email }
  );
  return data.credential;
};

const uploadStaffPhoto = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append("photo", file);
  const { data } = await privateClient.post<{ photoUrl: string }>(
    INSTITUTION_ADMIN_ENDPOINTS.UPLOADS.STAFF_PHOTO,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data.photoUrl;
};

// ---------- Salary ----------

const getSalaryComponents = async (institutionId: number): Promise<SalaryComponent[]> => {
  const { data } = await privateClient.get<{ components: SalaryComponent[] }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.SALARY.COMPONENTS(institutionId)
  );
  return data.components;
};

const createSalaryComponent = async (
  institutionId: number,
  payload: CreateSalaryComponentPayload
): Promise<SalaryComponent> => {
  const { data } = await privateClient.post<{ component: SalaryComponent }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.SALARY.COMPONENTS(institutionId),
    payload
  );
  return data.component;
};

const upsertSalaryStructure = async (
  institutionId: number,
  staffId: number,
  payload: UpsertSalaryStructurePayload
): Promise<SalaryStructure> => {
  const { data } = await privateClient.put<{ structure: SalaryStructure }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.SALARY.STRUCTURE(institutionId, staffId),
    payload
  );
  return data.structure;
};

const getSalaryStructure = async (
  institutionId: number,
  staffId: number,
  academicYearId: number
): Promise<SalaryStructure | null> => {
  try {
    const { data } = await privateClient.get<{ structure: SalaryStructure }>(
      INSTITUTION_ADMIN_ENDPOINTS.STAFF.SALARY.STRUCTURE(institutionId, staffId),
      { params: { academicYearId } }
    );
    return data.structure;
  } catch (err: any) {
    // No structure yet for this year is a normal, expected state — not an error.
    if (err?.response?.status === 404) return null;
    throw err;
  }
};

const getMonthlyPayable = async (
  institutionId: number,
  staffId: number,
  params: { academicYearId: number; year: number; month: number }
): Promise<MonthlyPayable | null> => {
  try {
    const { data } = await privateClient.get<{ monthlyPayable: MonthlyPayable }>(
      INSTITUTION_ADMIN_ENDPOINTS.STAFF.SALARY.MONTHLY_PAYABLE(institutionId, staffId),
      { params }
    );
    return data.monthlyPayable;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
};

const getSalaryStructureHistory = async (institutionId: number, staffId: number): Promise<SalaryStructure[]> => {
  const { data } = await privateClient.get<{ structures: SalaryStructure[] }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.SALARY.STRUCTURE_HISTORY(institutionId, staffId)
  );
  return data.structures;
};

// ---------- Assignments ----------

const getAssignments = async (
  institutionId: number,
  staffId: number,
  academicYearId: number
): Promise<StaffAssignment[]> => {
  const { data } = await privateClient.get<{ assignments: StaffAssignment[] }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.ASSIGNMENTS.LIST(institutionId, staffId),
    { params: { academicYearId } }
  );
  return data.assignments;
};

const createAssignment = async (
  institutionId: number,
  staffId: number,
  payload: CreateAssignmentPayload
): Promise<StaffAssignment> => {
  const { data } = await privateClient.post<{ assignment: StaffAssignment }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.ASSIGNMENTS.CREATE(institutionId, staffId),
    payload
  );
  return data.assignment;
};

const removeAssignment = async (institutionId: number, staffId: number, assignmentId: number): Promise<void> => {
  await privateClient.delete(INSTITUTION_ADMIN_ENDPOINTS.STAFF.ASSIGNMENTS.REMOVE(institutionId, staffId, assignmentId));
};

const getAssignmentHistory = async (institutionId: number, staffId: number): Promise<StaffAssignment[]> => {
  const { data } = await privateClient.get<{ assignments: StaffAssignment[] }>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.ASSIGNMENTS.HISTORY(institutionId, staffId)
  );
  return data.assignments;
};

// ---------- Attendance ----------

const markStaffAttendance = async (
  institutionId: number,
  staffId: number,
  payload: MarkStaffAttendancePayload
): Promise<StaffAttendanceRecord> => {
  const { data } = await privateClient.put<StaffAttendanceRecord>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.ATTENDANCE(institutionId, staffId),
    payload
  );
  return data;
};

const getStaffAttendanceRoster = async (
  institutionId: number,
  date: string
): Promise<StaffAttendanceRosterResponse> => {
  const { data } = await privateClient.get<StaffAttendanceRosterResponse>(
    INSTITUTION_ADMIN_ENDPOINTS.STAFF.ATTENDANCE_ROSTER(institutionId),
    { params: { date } }
  );
  return data;
};

export const staffService = {
  getInstitutionRoles,
  changeStaffRole,
  createInstitutionRole,
  updateInstitutionRole,
  deleteInstitutionRole,
  getStaff,
  getStaffByRole,
  createStaff,
  getStaffById,
  updateStaff,
  resetStaffPassword,
  changeStaffEmail,
  grantStaffLogin,
  uploadStaffPhoto,
  getSalaryComponents,
  createSalaryComponent,
  upsertSalaryStructure,
  getSalaryStructure,
  getSalaryStructureHistory,
  getMonthlyPayable,
  getAssignments,
  createAssignment,
  removeAssignment,
  getAssignmentHistory,
  markStaffAttendance,
  getStaffAttendanceRoster,
};